import type { LoginResponse } from './api';

export const PORTAL_API = (
  process.env.REACT_APP_STUDENT_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://siloemanager-production-938c.up.railway.app'
).replace(/\/$/, '');

export const PHOTO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const MAX_PHOTO_BYTES = 3 * 1024 * 1024;

export interface PortalSession {
  access: string;
  refresh: string;
  mustChangePassword: boolean;
}

export interface PortalScheduleSlot {
  day: 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
  start: string;
  end: string;
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Tu sesión terminó. Inicia sesión de nuevo.');
  }
}

export async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    return body?.errors?.[0]?.description || body?.message || fallback;
  } catch {
    return fallback;
  }
}

interface PortalClientOptions {
  storageKey: string;
  role: number;
  wrongRoleMessage: string;
  photoPath: string;
}

/** Sesión JWT de un portal (estudiante o docente) contra la API de Siloe. */
export function createPortalClient({ storageKey, role, wrongRoleMessage, photoPath }: PortalClientOptions) {
  function getSession(): PortalSession | null {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as PortalSession;
    } catch {
      return null;
    }
  }

  function saveSession(session: PortalSession) {
    sessionStorage.setItem(storageKey, JSON.stringify(session));
  }

  function logout() {
    sessionStorage.removeItem(storageKey);
  }

  async function login(email: string, password: string): Promise<PortalSession> {
    let response: Response;
    try {
      response = await fetch(`${PORTAL_API}/auth/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch {
      throw new Error('No se pudo conectar con el servidor. Intenta de nuevo.');
    }
    if (response.status === 400 || response.status === 401) {
      throw new Error('Usuario o contraseña incorrectos.');
    }
    if (!response.ok) {
      throw new Error('Error del servidor. Intenta más tarde.');
    }

    const data: LoginResponse = await response.json();
    if (data.user?.role !== role) {
      throw new Error(wrongRoleMessage);
    }
    const session: PortalSession = {
      access: data.access,
      refresh: data.refresh,
      mustChangePassword: Boolean(data.user?.must_change_password),
    };
    saveSession(session);
    return session;
  }

  async function refreshAccess(session: PortalSession): Promise<PortalSession | null> {
    try {
      const response = await fetch(`${PORTAL_API}/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: session.refresh }),
      });
      if (!response.ok) return null;
      const { access } = await response.json();
      const updated = { ...session, access };
      saveSession(updated);
      return updated;
    } catch {
      return null;
    }
  }

  async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
    let session = getSession();
    if (!session) throw new SessionExpiredError();

    const isForm = init.body instanceof FormData;
    const send = (token: string) =>
      fetch(`${PORTAL_API}${path}`, {
        ...init,
        headers: {
          ...(isForm ? {} : { 'Content-Type': 'application/json' }),
          ...init.headers,
          Authorization: `Bearer ${token}`,
        },
      });

    let response: Response;
    try {
      response = await send(session.access);
      if (response.status === 401) {
        session = await refreshAccess(session);
        if (!session) {
          logout();
          throw new SessionExpiredError();
        }
        response = await send(session.access);
      }
    } catch (err) {
      if (err instanceof SessionExpiredError) throw err;
      throw new Error('No se pudo conectar con el servidor. Intenta de nuevo.');
    }

    if (response.status === 401) {
      logout();
      throw new SessionExpiredError();
    }
    return response;
  }

  /** GET/POST que devuelve `data` del sobre de Siloe o lanza el mensaje de error del servidor. */
  async function request<T>(path: string, fallback: string, init: RequestInit = {}): Promise<T> {
    const response = await authedFetch(path, init);
    if (response.status === 403) {
      logout();
      throw new Error(await errorMessage(response, 'Esta cuenta no tiene acceso al portal.'));
    }
    if (!response.ok) {
      throw new Error(await errorMessage(response, fallback));
    }
    const body = await response.json();
    return body.data as T;
  }

  async function uploadPhoto(file: File): Promise<string> {
    if (!PHOTO_TYPES.includes(file.type)) {
      throw new Error('La foto debe ser PNG, JPG o WEBP.');
    }
    if (file.size > MAX_PHOTO_BYTES) {
      throw new Error('La foto no puede pesar más de 3 MB.');
    }
    const form = new FormData();
    form.append('photo', file);
    const data = await request<{ photo: string }>(photoPath, 'No se pudo subir la foto.', {
      method: 'POST',
      body: form,
    });
    return data.photo;
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    const response = await authedFetch('/auth/change-password/', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    if (response.status === 400) {
      throw new Error('La contraseña actual no es correcta.');
    }
    if (!response.ok) {
      throw new Error('No se pudo cambiar la contraseña. Intenta más tarde.');
    }
  }

  async function forceChangePassword(newPassword: string) {
    const response = await authedFetch('/auth/force-change-password/', {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword }),
    });
    if (!response.ok) {
      throw new Error('No se pudo guardar la contraseña. Intenta más tarde.');
    }
    const session = getSession();
    if (session) saveSession({ ...session, mustChangePassword: false });
  }

  return {
    getSession,
    logout,
    login,
    authedFetch,
    request,
    uploadPhoto,
    changePassword,
    forceChangePassword,
  };
}

export type PortalClient = ReturnType<typeof createPortalClient>;
